<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-flex>
        <dt-search-box @typing="searchValue" @reset-datatable="resetDataTable()" @clear-search-field="search = null" />
      </v-flex>
    </v-card>

    <v-card flat>
      <v-layout row>
        <v-layout row wrap justify-start align-end>
          <v-flex pa-2>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin:0 10px 0; height: 30px; width: 30px;"
                  :loading="loadThePrintBtn"
                  flat
                  fab
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 0; height: 30px; width: 30px"
                  fab
                  :loading="loadTheExportBtn"
                  flat
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row wrap justify-end>
          <v-flex xs3 pa-2>
            <v-menu v-model="startDateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="startDate"
                :label="$t('Start Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="start date"
                :error="errors.has('start date')"
                v-validate="'required|date_format:yyyy-MM-dd'"
              />
              <v-date-picker @change="startDateMenu = false" v-model="startDate" @input="updateDatatableResult()" />
            </v-menu>
          </v-flex>

          <v-flex xs3 pa-2>
            <v-menu v-model="endDateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="endDate"
                :label="$t('End Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="end date"
                :error="errors.has('end date')"
                v-validate="'required|date_format:yyyy-MM-dd'"
              />
              <v-date-picker @change="endDateMenu = false" v-model="endDate" @input="updateDatatableResult()" />
            </v-menu>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card px-4 pb-4>
      <v-data-table
        :headers="headers"
        :items="resultData"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <tr style="background-color: white !important;">
            <!--            <td rowspan="2">{{ props.item.general_journal_id }}</td>-->
            <td rowspan="2">{{ props.item.journal_code }}</td>
            <td rowspan="2">{{ props.item.journal_date }}</td>
            <td>{{ $t(props.item.debit_account_name.trim()) }}</td>
            <td>{{ props.item.debit | currency }}</td>
            <td></td>
          </tr>
          <tr style="background-color: white !important;">
            <td style="text-align: right">
              {{ $t(props.item.credit_account_name.trim()) }}
            </td>
            <td></td>
            <td>{{ props.item.credit | currency }}</td>
          </tr>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>
    </v-card>

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      dialogs: {
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      startDateMenu: false,
      startDate: this.$moment()
        .format()
        .substr(0, 10),
      endDateMenu: false,
      endDate: this.$moment()
        .format()
        .substr(0, 10),
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'account_chart_id',
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'general_journal_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('NO.'), value: 'journal_code' },
        { text: this.$t('Date'), value: 'journal_date' },
        { text: this.$t('Account Name'), value: '' },
        {
          text: `${this.$t('Debit')} (${localStorage.systemCurrency})`,
          value: 'debit'
        },
        {
          text: `${this.$t('Credit')} (${localStorage.systemCurrency})`,
          value: 'credit'
        }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('General Journal Report'),
      icon: 'fa-chart-line'
    })

    this.fetchOutletList()
    this.updateDatatableResult()
  },

  computed: {
    ...mapGetters({
      resultData: 'generalJournal/getJournalList'
    })
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'generalJournal/updateJournalList',
      fetchOutletList: 'outlets/setFullOutletsList'
    }),

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = [...this.headers]
      this.tableHeader.splice(2, 1)
      this.tableHeader.splice(2, 0, { text: this.$t('Debit Account Name'), value: 'debit_account_name' })
      this.tableHeader.splice(3, 0, { text: this.$t('Credit Account Name'), value: 'debit_account_name' })
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`report_general_journal`, {
        params: {
          is_paginated: 0,
          search: this.search,
          start_date: this.startDate,
          end_date: this.endDate
        }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        start_date: this.startDate,
        end_date: this.endDate,
        search: this.search
      }
      this.updateResult(payload)
        .then(() => {
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
      this.updateDatatableResult()
    }
  }
}
</script>

<style scoped />
